import { useState, type ComponentProps, type FC, type ReactNode } from 'react'
import { useDialogStore, type DialogOptions } from './dialogStore'
import { Footer } from './Footer'

type DialogContent = FC<{
  close: () => void
}>

export function showDialog(Content: DialogContent, options?: DialogOptions) {
  const { title, size, layout = 'default', description, hideCloseButton } = options ?? {}

  useDialogStore.setState({
    show: true,
    title,
    size,
    layout,
    description,
    hideCloseButton,
    content: <Content close={() => useDialogStore.getState().setShow(false)} />,
  })
}

type PromptDialogContent<T> = FC<{
  value?: T
  setValue: (value: T) => void
  setValid: (valid: boolean) => void
  close: () => void
}>

type PromptOptions = DialogOptions &
  Pick<ComponentProps<typeof Footer>, 'cancelLabel' | 'confirmLabel' | 'variant' | 'showCancel'>

const defaultFooterOptions: PromptOptions = {
  cancelLabel: 'Cancel',
  confirmLabel: 'Confirm',
  variant: 'primary',
  title: 'Title',
  layout: 'default',
  showCancel: true,
}

export function showPromptDialog<T>(Content: PromptDialogContent<T>, options?: PromptOptions) {
  const { title, description, size, layout, hideCloseButton, ...footerOptions } = {
    ...defaultFooterOptions,
    ...(options ?? {}),
  }

  return new Promise<T | undefined>((resolve, reject) => {
    showDialog(
      ({ close }: { close: () => void }) => {
        const [value, setValue] = useState<T>()
        const [valid, setValid] = useState<boolean>(false)
        return (
          <>
            <Content value={value} setValue={setValue} setValid={setValid} close={close} />
            <Footer
              {...footerOptions}
              disabled={!valid}
              onCancel={() => {
                reject()
                close()
              }}
              onConfirm={() => {
                resolve(value)
                close()
              }}
            />
          </>
        )
      },
      { title, size, layout, description, hideCloseButton }
    )
  })
}

export function showConfirmationPrompt(content: ReactNode, options?: PromptOptions) {
  const { title, description, size, layout, hideCloseButton, ...footerOptions } = {
    ...defaultFooterOptions,
    ...(options ?? {}),
  }

  return new Promise<boolean>((resolve, reject) => {
    showDialog(
      ({ close }: { close: () => void }) => {
        return (
          <>
            <div className="py-4 text-muted-foreground">{content}</div>
            <Footer
              {...footerOptions}
              onCancel={() => {
                close()
                reject()
              }}
              onConfirm={() => {
                resolve(true)
                close()
              }}
            />
          </>
        )
      },
      { title, size, layout, description, hideCloseButton }
    )
  })
}
